import React from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { Tooltip } from "@coval/ui/tooltip"
import { Clock, X } from "lucide-react"

import { MetricErrorStatus } from "@/types/api/MetricOutput"
import type { TargetCondition } from "@/types/metrics/contracts"
import type { RunRowLike } from "@/types/RunRow"

import {
  getAnnotationMetricColor,
  yesNoBadgeColorClasses,
  yesNoBadgeNAClasses,
  yesNoBadgeShapeClasses,
} from "../utils/annotationBadge"

export function getErrorTooltip(
  errorStatus: string,
  errorMessage: string,
): string | null {
  if (errorStatus === "CANCELLED" || errorStatus === "RUN_CANCELLED") {
    return "Run was canceled"
  }
  if (errorStatus === MetricErrorStatus.SKIPPED) {
    return "Metric was skipped"
  }
  if (
    errorStatus === MetricErrorStatus.IN_PROGRESS ||
    errorStatus === "QUEUED" ||
    errorStatus === MetricErrorStatus.IN_QUEUE
  ) {
    return null
  }
  if (errorStatus && errorStatus !== MetricErrorStatus.COMPLETED) {
    return errorMessage || "Metric encountered an error"
  }
  return null
}

export const AIReviewButton: React.FC<{
  metricId: string
  simulationOutputRow: RunRowLike
  targetCondition?: TargetCondition
}> = ({ metricId, simulationOutputRow, targetCondition }) => {
  const metricOutput = simulationOutputRow.metric_outputs[metricId]

  if (!metricOutput) {
    return <span>-</span>
  }

  const errorStatus = metricOutput.error_status || ""
  const errorMessage = metricOutput.error_message || ""

  if (errorStatus === MetricErrorStatus.IN_PROGRESS) {
    return (
      <Tooltip content="Metric is still running">
        <span
          role="status"
          aria-label="Metric is still running"
          className="flex items-center justify-center"
        >
          <svg
            aria-hidden
            className="h-5 w-5 text-gray-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </span>
      </Tooltip>
    )
  }

  if (
    (errorStatus as string) === "QUEUED" ||
    errorStatus === MetricErrorStatus.IN_QUEUE
  ) {
    return (
      <Tooltip content="Metric is queued for processing">
        <span
          role="status"
          aria-label="Metric is queued for processing"
          className="flex items-center justify-center"
        >
          <Clock aria-hidden className="h-5 w-5 text-gray-500" />
        </span>
      </Tooltip>
    )
  }

  const errorTooltip = getErrorTooltip(errorStatus, errorMessage)
  if (errorTooltip) {
    return (
      <Tooltip content={errorTooltip}>
        <span
          role="status"
          aria-label={errorTooltip}
          className="text-red-500 flex items-center justify-center"
        >
          <X aria-hidden className="h-5 w-5" />
        </span>
      </Tooltip>
    )
  }

  const result = metricOutput.string_value

  const getChipStyle = (value: string | null) => {
    if (value === ANNOTATION_VALUE.YES || value === ANNOTATION_VALUE.NO) {
      const color = getAnnotationMetricColor(value, targetCondition)
      return yesNoBadgeColorClasses[color]
    }
    return yesNoBadgeNAClasses
  }

  return (
    <div
      className={`inline-block ${yesNoBadgeShapeClasses} ${getChipStyle(result)}`}
      data-ai-review-button
    >
      {result === ANNOTATION_VALUE.UNKNOWN
        ? "N/A"
        : result === ANNOTATION_VALUE.YES
          ? "Yes"
          : result === ANNOTATION_VALUE.NO
            ? "No"
            : "N/A"}
    </div>
  )
}
